import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";

import Page from "src/components/Page";
import { sendEstimate } from "../Model/Estimate";

const steps = ["Contact Details", "Artwork Details", "Upload Photo"];

const categories = [
  "Painting",
  "Sculpture",
  "Furniture",
  "Jewelry",
  "Objet D arts",
  "Books",
];

const stepIcons = [
  <PersonOutlineOutlinedIcon />,
  <DescriptionOutlinedIcon />,
  <CameraAltOutlinedIcon />,
];

export default function RequestEstimate() {
  localStorage.removeItem("@storeAuctionLot");

  const isMobile = useMediaQuery("(max-width:900px)");

  const [activeStep, setActiveStep] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  const [fullname, setFullname] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phoneNumber, setPhonumber] = React.useState("");
  const [category, setCategory] = React.useState("");

  const [artist, setArtist] = React.useState("");
  const [title, setTitle] = React.useState("");
  const [year, setYear] = React.useState("");
  const [medium, setmedium] = React.useState("");
  const [details, setDetails] = React.useState("");

  const [frontImage, setFrontImage] = React.useState(null);
  const [backImage, setBackImage] = React.useState(null);

  const [errors, setErrors] = React.useState({});

  const compressImage = (file, maxWidth = 800, quality = 0.6) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = (event) => {
        img.src = event.target.result;
      };

      img.onload = () => {
        const scale = maxWidth / img.width;
        const canvas = document.createElement("canvas");

        canvas.width = maxWidth;
        canvas.height = img.height * scale;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: "image/jpeg",
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          quality
        );
      };
    });
  };

  const validateStep = () => {
    const nextErrors = {};

    if (activeStep === 0) {
      if (!fullname) nextErrors.fullname = true;
      if (!email) nextErrors.email = true;
      if (!phoneNumber) nextErrors.phoneNumber = true;
      if (!category) nextErrors.category = true;
    }

    if (activeStep === 1) {
      if (category !== "Jewelry" && !artist) nextErrors.artist = true;
      if (category !== "Jewelry" && !title) nextErrors.title = true;
      if (category !== "Jewelry" && !year) nextErrors.year = true;
      if (category !== "Jewelry" && !medium) nextErrors.medium = true;
      if (!details) nextErrors.details = true;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setErrors({});
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setErrors({});
    setMessage(null);
  };

  const submitValue = async (e) => {
    e.preventDefault();
    setMessage(null);

    if (!frontImage || !backImage) {
      setErrors({
        frontImage: !frontImage,
        backImage: !backImage,
      });
      return;
    }

    setLoading(true);

    try {
      let front = frontImage;
      let back = backImage;

      if (frontImage.size > 1000000) {
        front = await compressImage(frontImage);
      }

      if (backImage.size > 1000000) {
        back = await compressImage(backImage);
      }

      const dataForm = new FormData();

      dataForm.append("fullname", fullname);
      dataForm.append("email", email);
      dataForm.append("phoneNumber", phoneNumber);
      dataForm.append("artist", artist);
      dataForm.append("title", title);
      dataForm.append("year", year);
      dataForm.append("medium", medium);
      dataForm.append("details", details);
      dataForm.append("frontImage", front);
      dataForm.append("backImage", back);
      dataForm.append("category", category);

      const results = await sendEstimate(dataForm);

      setMessage(results);
      setFullname("");
      setEmail("");
      setPhonumber("");
      setArtist("");
      setTitle("");
      setYear("");
      setmedium("");
      setDetails("");
      setFrontImage(null);
      setBackImage(null);
      setCategory("");
      setErrors({});
      setActiveStep(0);
    } catch (error) {
      console.error("API ERROR:", error);
      setMessage("Submission failed. Please try again.");
    }

    setLoading(false);
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      backgroundColor: "#fff",
    },
    "& .MuiInputLabel-root": {
      fontSize: 14,
    },
  };

  const helperText =
    "If not available, kindly indicate 'Not Applicable'.";

  return (
    <Page title="Request for Estimate">
      <Box sx={{ bgcolor: "#fff", color: "#111" }}>
        {/* HERO */}
        <Box
          sx={{
            bgcolor: "#111",
            color: "#fff",
            textAlign: "center",
            px: 3,
            py: isMobile ? 6 : 8,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 4,
              textTransform: "uppercase",
              color: "#aaa",
              mb: 2,
            }}
          >
            León Gallery
          </Typography>

          <Typography
            sx={{
              fontSize: isMobile ? 38 : 58,
              fontWeight: 400,
              lineHeight: 1,
              mb: 2,
            }}
          >
            Request an Estimate
          </Typography>

          <Typography
            sx={{
              fontSize: 13,
              letterSpacing: 1.5,
              color: "#ccc",
            }}
          >
            Tell us about your item
          </Typography>
        </Box>

        <Container maxWidth="lg" sx={{ py: isMobile ? 5 : 8 }}>
          {/* INTRO */}
          <Box sx={{ maxWidth: 760, mx: "auto", textAlign: "center", mb: 6 }}>
            <Typography
              sx={{
                color: "#666",
                fontSize: 14,
                lineHeight: 1.8,
              }}
            >
              Complete the form below and our specialists will review the
              information provided. All fields are required unless otherwise
              indicated.
            </Typography>
          </Box>

          {/* STEPPER */}
          <Box sx={{ maxWidth: 850, mx: "auto", mb: 6 }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel={!isMobile}
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{
                "& .MuiStepLabel-label": {
                  fontSize: 12,
                  letterSpacing: 0.5,
                },
                "& .MuiStepIcon-root.Mui-active": {
                  color: "#111",
                },
                "& .MuiStepIcon-root.Mui-completed": {
                  color: "#111",
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    icon={
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          border: "1px solid #222",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: activeStep >= index ? "#111" : "#fff",
                          color: activeStep >= index ? "#fff" : "#111",
                        }}
                      >
                        {React.cloneElement(stepIcons[index], {
                          sx: { fontSize: 18 },
                        })}
                      </Box>
                    }
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          <Divider sx={{ mb: 6 }} />

          {/* FORM CARD */}
          <Box
            sx={{
              maxWidth: 900,
              mx: "auto",
              border: "1px solid #ddd",
              bgcolor: "#fafafa",
            }}
          >
            {activeStep < steps.length ? (
              <Box component="form" onSubmit={submitValue}>
                {/* STEP 1 */}
                {activeStep === 0 && (
                  <Box sx={{ p: isMobile ? 3 : 5 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        letterSpacing: 3,
                        textTransform: "uppercase",
                        color: "#888",
                        mb: 1,
                      }}
                    >
                      Step 01
                    </Typography>

                    <Typography sx={{ fontSize: 28, mb: 1 }}>
                      Contact Details
                    </Typography>

                    <Typography
                      sx={{ color: "#777", fontSize: 13, mb: 4 }}
                    >
                      Tell us how we can reach you.
                    </Typography>

                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          required
                          value={fullname}
                          error={errors.fullname}
                          helperText={errors.fullname ? helperText : ""}
                          onChange={(e) => setFullname(e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          required
                          value={email}
                          error={errors.email}
                          helperText={errors.email ? helperText : ""}
                          onChange={(e) => setEmail(e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Phone Number"
                          required
                          value={phoneNumber}
                          error={errors.phoneNumber}
                          helperText={errors.phoneNumber ? helperText : ""}
                          onChange={(e) => setPhonumber(e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          select
                          label="Select Category"
                          required
                          value={category}
                          error={errors.category}
                          helperText={
                            errors.category
                              ? "Please select a category."
                              : ""
                          }
                          onChange={(e) => setCategory(e.target.value)}
                          sx={fieldSx}
                        >
                          <MenuItem value="">Select Category</MenuItem>
                          {categories.map((item) => (
                            <MenuItem key={item} value={item}>
                              {item}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* STEP 2 */}
                {activeStep === 1 && (
                  <Box sx={{ p: isMobile ? 3 : 5 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        letterSpacing: 3,
                        textTransform: "uppercase",
                        color: "#888",
                        mb: 1,
                      }}
                    >
                      Step 02
                    </Typography>

                    <Typography sx={{ fontSize: 28, mb: 1 }}>
                      Artwork Details
                    </Typography>

                    <Typography
                      sx={{ color: "#777", fontSize: 13, mb: 4 }}
                    >
                      Provide the details available about your item.
                    </Typography>

                    <Grid container spacing={2.5}>
                      {category !== "Jewelry" && (
                        <>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Artist"
                              value={artist}
                              error={errors.artist}
                              helperText={errors.artist ? helperText : ""}
                              onChange={(e) => setArtist(e.target.value)}
                              sx={fieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Title"
                              value={title}
                              error={errors.title}
                              helperText={errors.title ? helperText : ""}
                              onChange={(e) => setTitle(e.target.value)}
                              sx={fieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Year"
                              value={year}
                              error={errors.year}
                              helperText={errors.year ? helperText : ""}
                              onChange={(e) => setYear(e.target.value)}
                              sx={fieldSx}
                            />
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Medium"
                              value={medium}
                              error={errors.medium}
                              helperText={errors.medium ? helperText : ""}
                              onChange={(e) => setmedium(e.target.value)}
                              sx={fieldSx}
                            />
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Other Details"
                          multiline
                          rows={5}
                          value={details}
                          error={errors.details}
                          helperText={errors.details ? helperText : ""}
                          onChange={(e) => setDetails(e.target.value)}
                          sx={fieldSx}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* STEP 3 */}
                {activeStep === 2 && (
                  <Box sx={{ p: isMobile ? 3 : 5 }}>
                    <Typography
                      sx={{
                        fontSize: 11,
                        letterSpacing: 3,
                        textTransform: "uppercase",
                        color: "#888",
                        mb: 1,
                      }}
                    >
                      Step 03
                    </Typography>

                    <Typography sx={{ fontSize: 28, mb: 1 }}>
                      Upload Photos
                    </Typography>

                    <Typography
                      sx={{ color: "#777", fontSize: 13, mb: 4 }}
                    >
                      Please provide clear front and back photographs of the
                      item.
                    </Typography>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Photo Front"
                          required
                          type="file"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ accept: "image/jpeg", multiple: true }}
                          onChange={(e) =>
                            setFrontImage(e.target.files[0] || null)
                          }
                          error={errors.frontImage}
                          helperText={
                            errors.frontImage
                              ? "Front image is required."
                              : "JPEG image"
                          }
                          sx={fieldSx}
                        />

                        <Box
                          sx={{
                            mt: 2,
                            height: 240,
                            border: "1px solid #ddd",
                            bgcolor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            alt="Front preview"
                            src={
                              frontImage
                                ? URL.createObjectURL(frontImage)
                                : "https://placehold.co/400x300?text=Front+Image"
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Photo Back"
                          required
                          type="file"
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ accept: "image/jpeg", multiple: true }}
                          onChange={(e) =>
                            setBackImage(e.target.files[0] || null)
                          }
                          error={errors.backImage}
                          helperText={
                            errors.backImage
                              ? "Back image is required."
                              : "JPEG image"
                          }
                          sx={fieldSx}
                        />

                        <Box
                          sx={{
                            mt: 2,
                            height: 240,
                            border: "1px solid #ddd",
                            bgcolor: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            alt="Back preview"
                            src={
                              backImage
                                ? URL.createObjectURL(backImage)
                                : "https://placehold.co/400x300?text=Back+Image"
                            }
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* ACTIONS */}
                <Box
                  sx={{
                    borderTop: "1px solid #ddd",
                    px: isMobile ? 3 : 5,
                    py: 2.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Button
                    color="inherit"
                    disabled={activeStep === 0 || loading}
                    onClick={handleBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{
                      borderRadius: 0,
                      textTransform: "none",
                    }}
                  >
                    Back
                  </Button>

                  <Box sx={{ flex: 1 }} />

                  <Button
                    variant="contained"
                    type={activeStep === steps.length - 1 ? "submit" : "button"}
                    onClick={
                      activeStep === steps.length - 1 ? undefined : handleNext
                    }
                    endIcon={
                      activeStep === steps.length - 1 ? (
                        <CheckCircleOutlineIcon />
                      ) : (
                        <ArrowForwardIcon />
                      )
                    }
                    disabled={loading}
                    sx={{
                      bgcolor: "#111",
                      borderRadius: 0,
                      px: 3,
                      py: 1.3,
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      fontSize: 11,
                      "&:hover": { bgcolor: "#333" },
                    }}
                  >
                    {activeStep === steps.length - 1 ? "Submit Request" : "Next"}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 6, textAlign: "center" }}>
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 50, mb: 2 }}
                />
                <Typography sx={{ fontSize: 28, mb: 2 }}>
                  All steps completed
                </Typography>
                <Typography sx={{ color: "#666", mb: 4 }}>
                  Your request has been submitted.
                </Typography>
                <Button
                  onClick={handleReset}
                  variant="outlined"
                  sx={{ borderRadius: 0 }}
                >
                  Submit Another Request
                </Button>
              </Box>
            )}
          </Box>

          {message !== null && (
            <Alert
              severity={
                typeof message === "string" &&
                message.toLowerCase().includes("failed")
                  ? "error"
                  : "success"
              }
              sx={{ maxWidth: 900, mx: "auto", mt: 3 }}
            >
              {message}
            </Alert>
          )}
        </Container>

        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </Page>
  );
}
